import React from 'react';
import { useStreamAuth } from '../hooks/useStreamAuth';
import UserProfile from './auth/UserProfile';
import LogoutButton from './auth/LogoutButton';

const StreamingDashboard = () => {
  const { user, permissions, userPreferences, streamingSettings } = useStreamAuth();

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <h1 className="text-2xl font-bold">ChillFree Dashboard</h1>
          <div className="flex items-center space-x-4">
            <UserProfile />
            <LogoutButton />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-6">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">
            Welcome back, {user?.name || user?.email}!
          </h2>
          <p className="text-gray-300">
            Manage your streaming experience and content preferences.
          </p>
        </div>

        {/* Dashboard Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          
          {/* User Info Card */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-xl font-semibold mb-4">Account Info</h3>
            <div className="space-y-2">
              <p><span className="text-gray-400">Email:</span> {user?.email}</p>
              <p><span className="text-gray-400">Roles:</span> {permissions.isAdmin ? 'Admin' : permissions.isStreamer ? 'Streamer' : permissions.isPremiumUser ? 'Premium' : 'Standard'}</p>
              <p><span className="text-gray-400">Member since:</span> {user?.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'N/A'}</p>
            </div>
          </div>

          {/* Streaming Permissions */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-xl font-semibold mb-4">Streaming Access</h3>
            <div className="space-y-2">
              <div className="flex items-center">
                <span className={`w-3 h-3 rounded-full mr-2 ${permissions.canViewPremium ? 'bg-green-500' : 'bg-red-500'}`}></span>
                <span>Premium Content</span>
              </div>
              <div className="flex items-center">
                <span className={`w-3 h-3 rounded-full mr-2 ${permissions.canStream ? 'bg-green-500' : 'bg-red-500'}`}></span>
                <span>Create Streams</span>
              </div>
              <div className="flex items-center">
                <span className={`w-3 h-3 rounded-full mr-2 ${permissions.canModerate ? 'bg-green-500' : 'bg-red-500'}`}></span>
                <span>Moderation Tools</span>
              </div>
            </div>
          </div>

          {/* Preferences */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-xl font-semibold mb-4">Preferences</h3>
            <div className="space-y-2">
              <p><span className="text-gray-400">Theme:</span> {userPreferences.theme}</p>
              <p><span className="text-gray-400">Language:</span> {userPreferences.language}</p>
              <p><span className="text-gray-400">Autoplay:</span> {userPreferences.autoplay ? 'On' : 'Off'}</p>
              <p><span className="text-gray-400">Quality:</span> {userPreferences.quality}</p>
            </div>
          </div>

          {/* Streaming Settings (if user can stream) */}
          {permissions.canStream && streamingSettings && (
            <div className="bg-gray-800 rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4">Streaming Settings</h3>
              <div className="space-y-2">
                <p><span className="text-gray-400">Max Resolution:</span> {streamingSettings.maxResolution}</p>
                <p><span className="text-gray-400">Max Bitrate:</span> {streamingSettings.maxBitrate} kbps</p>
                <p><span className="text-gray-400">Custom Thumbnails:</span> {streamingSettings.canUseCustomThumbnails ? 'Yes' : 'No'}</p>
                <p><span className="text-gray-400">Schedule Streams:</span> {streamingSettings.canScheduleStreams ? 'Yes' : 'No'}</p>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-xl font-semibold mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded transition-colors">
                Browse Content
              </button>
              {permissions.canStream && (
                <button className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded transition-colors">
                  Start Streaming
                </button>
              )}
              <button className="w-full bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded transition-colors">
                Account Settings
              </button>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-xl font-semibold mb-4">Recent Activity</h3>
            <div className="space-y-2 text-sm text-gray-300">
              <p>• Watched "Action Movie" - 2 hours ago</p>
              <p>• Updated profile settings - 1 day ago</p>
              <p>• Joined ChillFree - {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'Recently'}</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default StreamingDashboard;

